import { hideLoadingScreen, router, showLoadingScreen } from "..";
import { getPrices } from "../api/prices";
import { getSp2, putSp2 } from "../api/smartplug2s_{imei}";
import { deviceCard, formatSubscriptionStatus } from "../device/device_card";
import { locale, relativeTimeString, t } from "../locale";
import { addNotificationListener } from "../mobile";
import { state } from "../state";
import {
	errorToast,
	keyValueParagraph,
	successToast,
} from "../view/components";
import { Html } from "../view/html";
import { MaterialDesign } from "../view/material_design";
import { getSmartplug2Warnings } from "./sp2_warnings";

/**
 * @param {import("./sp2").Smartplug2} sp2
 */
async function formatSubscriptionPlan(sp2) {
	if (sp2.Subscription.Trialing) return locale.strings.trial;

	const { Smartplug2Month, Smartplug2Year } = await getPrices();

	switch (sp2.Subscription.PriceIds.Current) {
		case Smartplug2Month.Id:
			return locale.string({
				enGB: "Monthly",
				deDE: "Monatlich",
				frFR: "Mensuel",
				svSE: "Månadsvis",
			});
		case Smartplug2Year.Id:
			return locale.string({
				enGB: "Yearly",
				deDE: "Jährlich",
				frFR: "Annuel",
				svSE: "Årsvis",
			});
	}

	const expiryTime = new Date(sp2.Subscription.ExpiresAt);
	if (Date.now() < expiryTime.getTime()) return "";

	return locale.strings.none;
}

/**
 * @param {import("./sp2").Smartplug2} sp2
 */
async function smartplug2Card(sp2) {
	addNotificationListener("smartplug2", sp2.Imei, () => {
		getSp2(sp2.Imei).then((sp2) => {
			state.smartplug2s[sp2.Imei] = sp2;
		});
	});

	const powerOnIcon = MaterialDesign.icon({
		id: "flash_on",
		onClick: onClickTogglePower,
	});

	const powerOffIcon = MaterialDesign.icon({
		id: "flash_off",
		onClick: onClickTogglePower,
	});

	const card = deviceCard({
		deviceName: sp2.Metadata.Name,
		deviceType: "smartplug2",
		productName: "SmartPlug2",
		warnings: getSmartplug2Warnings(sp2),
		lines: [
			keyValueParagraph(
				locale.string({
					enGB: "Device status",
					deDE: "Gerätestatus",
					frFR: "État de l'appareil",
					svSE: "Enhetsstatus",
				}),
			),
			keyValueParagraph(
				t("sp2Card.power")(),
				sp2.DeviceConfig.OutputPower && sp2.TargetConfig.OutputPower
					? t("sp2Card.on")()
					: !sp2.DeviceConfig.OutputPower &&
							!sp2.TargetConfig.OutputPower
						? t("sp2Card.off")()
						: "?",
			),
			keyValueParagraph(
				locale.string({
					deDE: "- Belastung",
					enGB: "- Load",
					frFR: "- Charge",
					svSE: "- Last",
				}),
				`${sp2.Load} W`,
			),
			keyValueParagraph(
				locale.string({
					deDE: "- Verbrauch",
					enGB: "- Consumption",
					frFR: "- Consommation",
					svSE: "- Förbrukning",
				}),
				`${sp2.Consumption} Wh`,
			),
			keyValueParagraph(
				t("deviceCard.connection")(),
				sp2.SignalStrength < 70
					? t("deviceCard.connectionMajorDisruptions")()
					: sp2.SignalStrength < 80
						? t("deviceCard.connectionMinorDisruptions")()
						: t("deviceCard.connectionReliable")(),
			),
			keyValueParagraph(
				t("deviceCard.lastActive")(),
				relativeTimeString(sp2.LastUploadAt),
			),
			keyValueParagraph(locale.strings.subscription),
			keyValueParagraph(
				locale.string({
					enGB: "- Plan",
					deDE: "- Plan",
					frFR: "- Plan",
					svSE: "- Plan",
				}),
				await formatSubscriptionPlan(sp2),
			),
			keyValueParagraph(...formatSubscriptionStatus(sp2.Subscription)),
		],
		buttons: [
			Html.anchor(
				{},
				sp2.TargetConfig.OutputPower ? powerOnIcon : powerOffIcon,
			),
			Html.anchor(
				{},
				MaterialDesign.icon({
					id: "monitoring",
					onClick: () => router.jump(`/smartplug2/${sp2.Imei}/logs`),
				}),
			),
			Html.anchor(
				{},
				MaterialDesign.icon({
					id: "settings",
					onClick: () => router.jump(`/smartplug2/${sp2.Imei}`),
				}),
			),
		],
	});

	function onClickTogglePower() {
		showLoadingScreen();
		sp2.TargetConfig.OutputPower = !sp2.TargetConfig.OutputPower;
		putSp2(sp2)
			.then((sp2) => {
				if (sp2.TargetConfig.OutputPower)
					powerOffIcon.replaceWith(powerOnIcon);
				else powerOnIcon.replaceWith(powerOffIcon);

				state.smartplug2s[sp2.Imei] = sp2;
				successToast();
			})
			.catch(errorToast)
			.finally(hideLoadingScreen);
	}

	return card;
}

export { smartplug2Card };
